import { resolveGearboxBadge } from "../../components/APanel/CalendarExtreme/extremeReservationRows";
import { normalizeColorToCss } from "../../components/APanel/CalendarExtreme/extremeEventColors";

export function normalizeSlotVisualColor(value) {
   return (
      normalizeColorToCss(value) ||
      String(value || "")
         .trim()
   );
}

export function buildReservationStatusMeta({
   meta = "",
   isFavorite = false,
   isImportant = false,
   gearbox = "",
   extraTags = [],
} = {}) {
   const parts = String(meta || "")
      .split(" · ")
      .map((part) => part.trim())
      .filter((part) => part && part !== "⁂" && part !== "‼");

   const gearboxBadge = resolveGearboxBadge(gearbox);
   if (gearboxBadge && !parts.includes(gearboxBadge)) {
      parts.push(gearboxBadge);
   }

   for (const tag of extraTags) {
      const normalized = String(tag || "").trim();
      if (normalized && !parts.includes(normalized)) {
         parts.push(normalized);
      }
   }

   if (isFavorite === true && !parts.includes("⁂")) parts.push("⁂");
   if (isImportant === true && !parts.includes("‼")) parts.push("‼");

   return parts.join(" · ");
}

function isPlaceholderTitle(value) {
   const text = String(value || "").trim();
   return !text || text.toLowerCase() === "rezervare";
}

/**
 * Overlay sparse edit/socket patches onto the worker slot item so name/phone/meta stay intact.
 */
export function mergeSlotIndexItem(baseItem, patchItem) {
   if (!baseItem) return patchItem || null;
   if (!patchItem) return baseItem;

   const merged = { ...baseItem, ...patchItem };
   const baseTitle = String(baseItem.title || "").trim();
   const patchTitle = String(patchItem.title || "").trim();

   if (isPlaceholderTitle(patchTitle) && !isPlaceholderTitle(baseTitle)) {
      merged.title = baseTitle;
   }

   if (!String(patchItem.phone || "").trim() && String(baseItem.phone || "").trim()) {
      merged.phone = baseItem.phone;
   }

   const patchPrivateMessage = String(patchItem.privateMessage || "").trim();
   const patchNote = String(patchItem.note || "").trim();
   const baseNote = String(baseItem.note || "").trim();

   if (!patchNote && baseNote) {
      merged.note = baseNote;
   } else if (
      patchPrivateMessage &&
      patchNote === patchPrivateMessage &&
      baseNote &&
      baseNote !== patchPrivateMessage
   ) {
      const basePrivateMessage = String(baseItem.privateMessage || "").trim();
      let profileNote = "";
      if (basePrivateMessage && baseNote.includes(basePrivateMessage)) {
         profileNote = baseNote
            .replace(basePrivateMessage, "")
            .replace(/^[\s;]+|[\s;]+$/g, "")
            .trim();
      } else if (baseNote.includes(";")) {
         const segments = baseNote.split(";").map((part) => part.trim()).filter(Boolean);
         profileNote = segments.length > 1 ? segments.slice(1).join("; ") : "";
      }
      merged.note = profileNote
         ? `${patchPrivateMessage}; ${profileNote}`
         : patchPrivateMessage;
   }

   if (!String(patchItem.timeText || "").trim() && String(baseItem.timeText || "").trim()) {
      merged.timeText = baseItem.timeText;
   }
   if (
      !String(patchItem.prefInstructor || "").trim() &&
      String(baseItem.prefInstructor || "").trim()
   ) {
      merged.prefInstructor = baseItem.prefInstructor;
   }
   if (!String(patchItem.userId || "").trim() && String(baseItem.userId || "").trim()) {
      merged.userId = baseItem.userId;
   }

   // Optimistic/socket patches don't know the preferred-instructor initials;
   // an empty value must never wipe what the month snapshot computed.
   if (
      !String(patchItem.prefInstructor || "").trim() &&
      String(baseItem.prefInstructor || "").trim()
   ) {
      merged.prefInstructor = baseItem.prefInstructor;
   }
   if (
      (!Array.isArray(patchItem.userCandidates) || !patchItem.userCandidates.length) &&
      Array.isArray(baseItem.userCandidates) &&
      baseItem.userCandidates.length
   ) {
      merged.userCandidates = baseItem.userCandidates;
   }

   merged.meta = buildReservationStatusMeta({
      meta: String(patchItem.meta || "").trim() ? patchItem.meta : baseItem.meta,
      isFavorite: merged.isFavorite === true,
      isImportant: merged.isImportant === true,
      gearbox: String(merged.gearbox || baseItem.gearbox || "").trim(),
   });
   merged.color = normalizeSlotVisualColor(merged.color || baseItem.color);

   return merged;
}

/**
 * Builds a slot-index visual item for instant optimistic grid paint (paste/create).
 */
export function buildOptimisticSlotVisualItem({
   id,
   userId,
   instructorId,
   title = "Rezervare",
   phone = "",
   note = "",
   privateMessage = "",
   color = "",
   isFavorite = false,
   isImportant = false,
   isCanceled = false,
   sector = "",
   gearbox = "",
   hasSectorMismatch = false,
   timeText = "",
   meta = "",
   prefInstructor = "",
} = {}) {
   const safeUserId = String(userId ?? "").trim();
   const favorite = isFavorite === true;
   const important = isImportant === true;
   const canceled = isCanceled === true;
   const resolvedMeta = buildReservationStatusMeta({
      meta,
      isFavorite: favorite,
      isImportant: important,
      gearbox,
   });

   return {
      id: String(id ?? "").trim(),
      userId: safeUserId,
      userCandidates: safeUserId ? [safeUserId] : [],
      instructorId: String(instructorId ?? "").trim(),
      title: String(title || "Rezervare").trim() || "Rezervare",
      phone: String(phone || "").trim(),
      note: String(note || "").trim(),
      privateMessage: String(privateMessage || "").trim(),
      meta: resolvedMeta,
      color: normalizeSlotVisualColor(color),
      isFavorite: favorite,
      isImportant: important,
      isCanceled: canceled,
      sector: String(sector || "").trim(),
      gearbox: String(gearbox || "").trim(),
      hasSectorMismatch: hasSectorMismatch === true,
      prefInstructor: String(prefInstructor || "").trim(),
      timeText: String(timeText || "").trim(),
   };
}
